import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b6d5aa8e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tree-container" }
const _hoisted_2 = {
  key: 1,
  class: "tree"
}

import { ref, computed, watch, Ref } from 'vue';
import { DisplayableKeys } from '@/services/SorterService';
import {ExpandState, labels} from '@/services/composables/TableExpandableColumns';
import TableExpandableTree from './TableExpandableTree.vue';
import TableExpandable from './TableExpandable.vue';
import MultiStateButton from './MultiStateButton.vue';
import Spinner from '../Spinner.vue';
import { KeyTypeKeys } from '../TablePersistentChecking.vue';
import { ComponentExposed } from 'vue-component-type-helpers';
/** static part of data */
export interface TableExTreeDefItem<T, P extends KeyTypeKeys<T>|undefined=KeyTypeKeys<T>, C extends KeyTypeKeys<T>|undefined=KeyTypeKeys<T>> {
    id?: string;
    title: string;
    cols: DisplayableKeys<T>[];
    expandedCols?: DisplayableKeys<T>[];
    idKey?: P;
    parentIdKey?: C;
    filterByDataOnly?: boolean;
    /** number of lines of gap between tables root */
    rowGap?: number;
    filter?: (x: T)=>boolean;
    filterDataUpdateCallback?: (x: T[NonNullable<P>][])=>void;
    children?: TableExTreeDefItem<any, any, any>[];
}
/** dynamic part */
export interface TableExTreeData<T> {
    rows?: T[];
    hasError?: boolean;
    children?: TableExTreeData<any>[];
}
interface TableExTreeItemWithFilterData<T, P extends KeyTypeKeys<T>|undefined, C extends KeyTypeKeys<T>|undefined> extends TableExTreeDefItem<T, P, C> {
    idKey: NonNullable<P>;
    children: TableExTreeDefItem<any>[];
}

export default /*#__PURE__*/_defineComponent({
  __name: 'TableExpandableTree',
  props: {
    def: {},
    dat: {},
    filterData: {},
    hasExpandAllCols: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

_useCssVars(_ctx => ({
  "4ff1f08e": (`${props.def.rowGap}lh`),
  "a64321a0": (treeGap.value)
}))

const props = __props;
const emits = __emit;
__expose<{
    getTotalExpandState(): ExpandState;
    expandAllCols(): void;
    collapseAllCols(): void;
}>({
    getTotalExpandState, expandAllCols, collapseAllCols
});
const children = ref<ComponentExposed<typeof TableExpandableTree>[]>();
const tableExpandState = ref<ExpandState>();
// must all be expanded to be considered expanded
const expandState = computed(()=>(
    (children.value?.some(x=>x.getTotalExpandState()===ExpandState.Collapsed)) ||
            tableExpandState.value===ExpandState.Collapsed) ?
                ExpandState.Collapsed :
                ExpandState.Expanded);
const filteredRows = ref(getFilteredRows()) as Ref<T[]|undefined>;
const tableHasFilterData = computed(()=>hasFilterData(props.def));
const tableHasCheckBox = computed(()=>tableHasFilterData.value&&!props.def.filterByDataOnly);
const treeGap = computed(()=>props.def.rowGap?`calc(10px + ${props.def.rowGap}lh)`:'10px');
function hasFilterData(def: TableExTreeDefItem<T,P,C>): def is TableExTreeItemWithFilterData<T,P,C> {
    return !(!def.children||!def.idKey||!def.children.some(x=>x.parentIdKey));
}
watch([()=>props.def.parentIdKey, ()=>props.filterData, ()=>props.dat.rows, ()=>props.def.filter], ([a,b,c,e],[aOld,bOld,cOld,eOld])=>{
    if (!(b?.length===0||bOld?.length===0)||!(c?.length===0||cOld?.length===0)||e!=eOld) updateFilteredRows();
});
const checkedFilterData = ref<T[NonNullable<P>][]>();
const filterDataForChildren = computed(()=>
    tableHasFilterData.value ? 
        tableHasCheckBox.value ?
            checkedFilterData.value :
            //@ts-expect-error def is TableExTreeItemWithFilterDat...
            filteredRows.value?.map(x=>x[props.def.idKey]) :
        undefined
);
function getFilteredRows() {
    let filtered = props.dat.rows;
    const parentIdKey = props.def.parentIdKey;
    if (parentIdKey) {
        const filterData = props.filterData;
        if (filterData) {
            filtered = filtered?.filter(x=>filterData.includes(x[parentIdKey]));
        }
    }
    if (props.def.filter) {
        filtered = filtered?.filter(props.def.filter);
    }
    return filtered;
}
function updateFilteredRows() {
    const backup = filteredRows.value;
    const rows = getFilteredRows();
    filteredRows.value = rows;
    const k = props.def.idKey;
    if (props.def.filterDataUpdateCallback && k) {
        const oldFilter = backup?.map(x=>x[k]).sort() ?? [];
        const newFilter = rows?.map(x=>x[k]).sort() ?? [];
        if (oldFilter.length !== newFilter.length || oldFilter.some((x,i)=>x!==newFilter[i])) {
            props.def.filterDataUpdateCallback(newFilter);
        }
    } 
}
function assignCheckedValues(checked: T[NonNullable<P>][]) {
    checkedFilterData.value = checked;
    props.def.filterDataUpdateCallback?.(checked);
}
function getChildDat(i: number) {
    const child = props.dat.children?.[i];
    if (props.dat.hasError) return {
        hasError: true,
        children: child?.children
    }
    const rows = child?.rows ? child.rows : props.dat.rows ? [] : undefined;
    return {
        rows,
        children: child?.children
    }
}
function getTotalExpandState() {
    return expandState.value;
}
function expandAllCols() {
    tableExpandState.value = ExpandState.Expanded;
    children.value?.forEach(x=>x.expandAllCols());
}
function collapseAllCols() {
    tableExpandState.value = ExpandState.Collapsed;
    children.value?.forEach(x=>x.collapseAllCols());
}
function expandButtonAction(val: unknown) {
    if (val === ExpandState.Expanded) expandAllCols();
    else collapseAllCols();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.dat.rows||_ctx.dat.hasError)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_ctx.hasExpandAllCols)
            ? (_openBlock(), _createBlock(MultiStateButton, {
                key: 0,
                "model-value": expandState.value,
                "onUpdate:modelValue": expandButtonAction,
                states: _unref(labels),
                style: {"font-size":"0.6rem"}
              }, null, 8, ["model-value", "states"]))
            : _createCommentVNode("", true),
          _createVNode(TableExpandable, {
            modelValue: tableExpandState.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tableExpandState).value = $event)),
            id: _ctx.def.id,
            style: {"flex":"0"},
            rows: filteredRows.value,
            cols: _ctx.def.cols,
            title: _ctx.def.title,
            "expanded-cols": _ctx.def.expandedCols,
            HasCheckBox: tableHasCheckBox.value,
            ColKey: _ctx.def.idKey,
            CheckCallBack: tableHasCheckBox.value?assignCheckedValues:undefined,
            HasError: _ctx.dat.hasError,
            ReturnKey: true,
            DisableCheckPersistentAcrossRefresh: "",
            CheckByDefault: "",
            CheckIncludesNonDisplayed: ""
          }, null, 8, ["modelValue", "id", "rows", "cols", "title", "expanded-cols", "HasCheckBox", "ColKey", "CheckCallBack", "HasError"]),
          (_ctx.def.children)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.def.children, (child, i) => {
                  return (_openBlock(), _createElementBlock("li", null, [
                    _createVNode(TableExpandableTree, {
                      ref_for: true,
                      ref_key: "children",
                      ref: children,
                      def: child,
                      dat: getChildDat(i),
                      filterData: filterDataForChildren.value
                    }, null, 8, ["def", "dat", "filterData"])
                  ]))
                }), 256))
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createBlock(Spinner, { key: 1 }))
  ]))
}
}

})